"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SquareCheck as CheckSquare, ShieldAlert, Cpu, Fan, Cable } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
interface ChecklistItem {
  id: string;
  itemNumber: string;
  title: string;
  description: string;
  iconName: string;
}
const CHECKLIST_ITEMS: ChecklistItem[] = [{
  id: "item-1",
  itemNumber: "ITEM 01",
  title: "Bull & Motion Base",
  description: "Confirm that the listing includes the complete primary machine assembly.",
  iconName: "Cpu"
}, {
  id: "item-2",
  itemNumber: "ITEM 02",
  title: "Operator Controller",
  description: "Check whether the controller shown or described is included.",
  iconName: "CheckSquare"
}, {
  id: "item-3",
  itemNumber: "ITEM 03",
  title: "Safety Arena & Blower",
  description: "Confirm whether the inflatable safety area and blower come with the machine.",
  iconName: "Fan"
}, {
  id: "item-4",
  itemNumber: "ITEM 04",
  title: "Cables & Accessories",
  description: "Check which cables, remotes, covers, accessories or documentation are included.",
  iconName: "Cable"
}];
export default function EquipmentInclusions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="usedbullequipmentinclusions" data-nav="light" className="relative w-full bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 lg:mb-16">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground mb-6 leading-none">
            What Comes With a Used Mechanical Bull?
          </h2>

          <div className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            <p className="font-semibold text-foreground">
              Never assume a used machine includes everything that originally came with it.
            </p>
            <p>
              A pre-owned package may include the bull body, motion base, controller, inflatable safety area, blower, cables and accessories. Another listing may include only part of that equipment. Before comparing prices, check the included-equipment list carefully.
            </p>
          </div>
        </div>

        {/* 4-Card Industrial Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
          {CHECKLIST_ITEMS.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="h-full">
              <Card className="h-full rounded-none border border-border bg-card text-card-foreground p-0 gap-0 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/80 hover:shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-end border-b border-border px-5 py-3.5 bg-muted/40">
                    <span className="text-primary">
                      {item.iconName === "Cpu" && <Cpu className="size-4" />}
                      {item.iconName === "CheckSquare" && <CheckSquare className="size-4" />}
                      {item.iconName === "Fan" && <Fan className="size-4" />}
                      {item.iconName === "Cable" && <Cable className="size-4" />}
                    </span>
                  </div>

                  <CardHeader className="p-5 pb-2">
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-5 pt-0">
                    <p className="text-sm text-muted-foreground leading-normal">
                      {item.description}
                    </p>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Industrial Caution / Advisory Callout Bar */}
        <div className="relative border border-border border-l-4 border-l-primary bg-card text-card-foreground p-5 sm:p-6 shadow-xs">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center justify-center size-10 bg-primary/10 border border-primary/20 shrink-0 text-primary">
              <ShieldAlert className="size-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="rounded-none border-primary/40 text-primary font-mono text-2xs uppercase tracking-wider px-1.5 py-0 font-semibold">
                  BUYER NOTE
                </Badge>
              </div>
              <p className="text-sm sm:text-base font-medium text-foreground">
                A lower price can look attractive until you discover that important equipment must be purchased separately.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}